function getCartApiUrls() {
  const routesElement = document.querySelector('[data-kl-cart-routes]');
  const cartUrl = routesElement?.dataset.klCartUrl || '/cart';
  const changeUrl = routesElement?.dataset.klCartChangeUrl || '/cart/change';

  return { addUrl: `${cartUrl}/add.js`, changeUrl: `${changeUrl}.js` };
}

let isGiftRequestInFlight = false;

function getClampedPercent(rawValue) {
  const parsedValue = Number.parseFloat(rawValue);

  if (Number.isNaN(parsedValue)) return 0;
  if (parsedValue < 0) return 0;
  if (parsedValue > 100) return 100;

  return parsedValue;
}

function getCurrencyRate() {
  try {
    const rate = Number.parseFloat(Shopify.currency.rate);

    return Number.isNaN(rate) || rate <= 0 ? 1 : rate;
  } catch (error) {
    return 1;
  }
}

function getThresholdInCartCurrency(threshold, rate) {
  return threshold * 100 * rate;
}

const cartMoneyFormatters = new Map();

function formatCartMoney(amountInMinorUnits, currencyCode) {
  try {
    if (!cartMoneyFormatters.has(currencyCode)) {
      cartMoneyFormatters.set(
        currencyCode,
        new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: currencyCode,
          currencyDisplay: 'narrowSymbol',
          maximumFractionDigits: 0,
        }),
      );
    }

    return cartMoneyFormatters.get(currencyCode).format(Math.ceil(amountInMinorUnits / 100));
  } catch (error) {
    console.debug('Cart drawer could not format a reward amount', { currencyCode, error });
    return '';
  }
}

function getSteppedProgressPercent(thresholdsInCartCurrency, cartTotal) {
  if (!thresholdsInCartCurrency.length) return 0;

  const stepPercent = 100 / thresholdsInCartCurrency.length;
  let previousThreshold = 0;

  for (let index = 0; index < thresholdsInCartCurrency.length; index += 1) {
    const threshold = thresholdsInCartCurrency[index];

    if (cartTotal >= threshold) {
      previousThreshold = threshold;
      continue;
    }

    const stepSpan = threshold - previousThreshold;
    const stepRatio = stepSpan > 0 ? (cartTotal - previousThreshold) / stepSpan : 0;

    return getClampedPercent((index + stepRatio) * stepPercent);
  }

  return 100;
}

function getMixedReachedPercent(thresholdsInCartCurrency, cartTotal, eligibleTotal) {
  if (!thresholdsInCartCurrency.length) return 0;

  const stepPercent = 100 / thresholdsInCartCurrency.length;
  let lastReachedStep = 0;

  thresholdsInCartCurrency.forEach((threshold, index) => {
    const total = index === 0 ? eligibleTotal : cartTotal;

    if (threshold > 0 && total >= threshold) lastReachedStep = index + 1;
  });

  return getClampedPercent(lastReachedStep * stepPercent);
}

/*
  Confetti on a reward tier the shopper has cleared.

  It is owed on two occasions: a tier that was not reached before now is, and the
  drawer opening on a cart that has already cleared one. The second is the reason a
  shopper who earned free shipping on a previous page still gets the celebration —
  the tier was cleared while they were not looking at the bar.

  Which makes this more than a call at the point `is-reached` is set. The drawer is
  re-rendered wholesale after every cart change, so "newly" cannot be read off the
  DOM — the count reached last time is remembered here, in module scope, which
  survives the re-render the elements do not. And Dawn opens the drawer a tick AFTER
  it swaps the new markup in, so at the moment a tier lights up the canvas is still
  inside a `visibility: hidden` panel and the burst would be drawn unseen. A burst
  that cannot be drawn yet is kept owed and flushed when the panel opens.
*/
const cartRewardReachedCounts = new Map();
const openCartRoots = new WeakSet();
let isCartConfettiOwed = false;

function getCartRewardsScopeName(rewardsElement) {
  return rewardsElement.closest('[data-kl-cart-drawer]') ? 'cart-drawer' : 'cart-page';
}

function flushCartConfetti() {
  if (!isCartConfettiOwed) return;

  const reachedRewardElements = document.querySelectorAll('[data-kl-cart-reward].is-reached');
  const lastReachedElement = reachedRewardElements[reachedRewardElements.length - 1];
  const originElement = lastReachedElement?.querySelector('[data-kl-cart-reward-node]');

  /* No node to throw from means the markup is not ready rather than that the
     celebration is off: the strip is re-rendered without `is-reached`, which this file
     applies, so a flush landing in that window would otherwise forget the burst. It
     stays owed for the pass that can draw it. Whether anything is still worth
     celebrating is settled in noteCartRewardsReached, which has the tier count. */
  if (!originElement) return;

  const didFire = document.dispatchEvent(
    new CustomEvent('kl-confetti:fire', {
      cancelable: true,
      detail: { scope: 'cart', originElement },
    }),
  );

  if (didFire) isCartConfettiOwed = false;
}

function noteCartRewardsReached(rewardsElement, reachedCount) {
  const scopeName = getCartRewardsScopeName(rewardsElement);

  /* A page arriving with a tier already cleared counts as having cleared it: the
     baseline is nothing reached, not whatever this first pass happens to find. On the
     cart page that fires here; in the drawer the burst is owed until it opens. */
  const previousReachedCount = cartRewardReachedCounts.get(scopeName) || 0;

  cartRewardReachedCounts.set(scopeName, reachedCount);

  /* The one place that can say the celebration is off, because it is the one that
     knows how many tiers are standing. */
  if (!reachedCount) isCartConfettiOwed = false;

  if (reachedCount <= previousReachedCount) return;

  isCartConfettiOwed = true;
  flushCartConfetti();
}

/*
  The strip is only rendered for a cart that has something in it, so emptying the cart
  takes it away and applyRewardProgress never runs. Left alone the count reached last
  time would stand, and because the count only ever ratchets upward, the next tier
  cleared would be measured against a cart that no longer exists — empty the cart,
  start again, and the first tier is judged already celebrated for the rest of the page
  view. No strip means no tiers reached.
*/
function noteCartRewardsAbsent() {
  document.querySelectorAll('[data-kl-cart-drawer], [data-kl-cart-page]').forEach((rootElement) => {
    if (rootElement.querySelector('[data-kl-cart-rewards]')) return;

    const scopeName = rootElement.matches('[data-kl-cart-drawer]') ? 'cart-drawer' : 'cart-page';

    cartRewardReachedCounts.set(scopeName, 0);
  });

  /* Owed is one flag for the whole document, so it is only safe to drop once no strip
     anywhere could still be the one the burst belongs to. A burst held back for a gift
     is owed across exactly this kind of re-render, and must survive it. */
  if (!document.querySelector('[data-kl-cart-rewards]')) isCartConfettiOwed = false;
}

/* Dawn opens the drawer by putting `active` on its root. Every open with a tier
   already cleared earns a burst, so the celebration is tied to the shopper looking at
   the bar rather than to the one cart change that happened to clear it. */
function noteCartRootClassChange(cartRootElement) {
  const isOpen = cartRootElement.classList.contains('active');
  const wasOpen = openCartRoots.has(cartRootElement);

  if (isOpen) openCartRoots.add(cartRootElement);
  else openCartRoots.delete(cartRootElement);

  if (!isOpen) return;

  if (!wasOpen && document.querySelector('[data-kl-cart-reward].is-reached')) {
    isCartConfettiOwed = true;
  }

  flushCartConfetti();
}

function applyRewardProgress(rewardsElement) {
  const fillElement = rewardsElement.querySelector('[data-kl-cart-rewards-fill]');
  const reachedElement = rewardsElement.querySelector('[data-kl-cart-rewards-reached]');
  const rewardElements = Array.from(rewardsElement.querySelectorAll('[data-kl-cart-reward]'));

  if (!fillElement) return;
  if (!rewardElements.length) return;

  const rate = getCurrencyRate();
  const currencyCode = rewardsElement.dataset.klCartRewardsCurrency;
  const cartTotal = Number.parseInt(rewardsElement.dataset.klCartRewardsTotal, 10) || 0;
  const eligibleTotal =
    Number.parseInt(rewardsElement.dataset.klCartRewardsEligibleTotal, 10) || 0;
  const awayTemplate = rewardsElement.dataset.klCartRewardsAwayTemplate || '';

  const thresholdsInCartCurrency = rewardElements.map((rewardElement) =>
    getThresholdInCartCurrency(Number.parseFloat(rewardElement.dataset.klCartRewardThreshold) || 0, rate),
  );

  const stepPercent = 100 / rewardElements.length;

  fillElement.style.setProperty(
    '--kl-cart-rewards-progress',
    `${getSteppedProgressPercent(thresholdsInCartCurrency, cartTotal)}%`,
  );

  if (reachedElement) {
    reachedElement.style.setProperty(
      '--kl-cart-rewards-reached',
      `${getMixedReachedPercent(thresholdsInCartCurrency, cartTotal, eligibleTotal)}%`,
    );
  }

  let reachedCount = 0;

  rewardElements.forEach((rewardElement, index) => {
    const positionPercent = getClampedPercent((index + 1) * stepPercent);

    rewardElement.style.setProperty('--kl-cart-reward-position', `${positionPercent}%`);

    const thresholdInCartCurrency = thresholdsInCartCurrency[index];
    const rewardTotal = index === 0 ? eligibleTotal : cartTotal;
    const isReached = thresholdInCartCurrency > 0 && rewardTotal >= thresholdInCartCurrency;
    const statusElement = rewardElement.querySelector('[data-kl-cart-reward-status]');

    rewardElement.classList.toggle('is-reached', isReached);

    if (isReached) reachedCount += 1;

    if (!statusElement) return;

    const nextStatus = isReached
      ? ''
      : awayTemplate.replace(
          '[amount]',
          formatCartMoney(thresholdInCartCurrency - rewardTotal, currencyCode),
        );

    if (statusElement.textContent !== nextStatus) statusElement.textContent = nextStatus;
  });

  noteCartRewardsReached(rewardsElement, reachedCount);
}

async function postCartChange(url, requestBody) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(requestBody),
  });

  if (response.ok) return;
  let cartMessage = '';

  try {
    const errorBody = await response.json();

    cartMessage = errorBody.description || errorBody.message || '';
  } catch (error) {
    cartMessage = '(no JSON body)';
  }

  throw new Error(`Cart responded ${response.status}: ${cartMessage}`);
}

async function renderCartDrawer() {
  if (!document.querySelector('#CartDrawer')) {
    const renderDetail = { renderPromises: [] };

    document.dispatchEvent(new CustomEvent('kl:cart:render', { detail: renderDetail }));
    await Promise.all(renderDetail.renderPromises);
    return;
  }

  const sectionResponse = await fetch(`${window.location.pathname}?section_id=cart-drawer`);

  if (!sectionResponse.ok) throw new Error(`Section responded ${sectionResponse.status}`);

  const sectionHtml = await sectionResponse.text();
  const sectionDocument = new DOMParser().parseFromString(sectionHtml, 'text/html');
  const freshDrawer = sectionDocument.querySelector('#CartDrawer');
  const liveDrawer = document.querySelector('#CartDrawer');

  if (!freshDrawer || !liveDrawer) return;

  /* A burst in flight is drawing on a canvas inside this markup, and replacing the
     markup takes it away mid-air. That is the whole reason a gift tier's celebration
     used to wait for the gift to land first, and the wait is what reads as the bar
     moving, then the gift arriving, then — late — the confetti.

     Carrying the element across instead keeps the same canvas, the same bitmap and the
     same running loop on the page, so a burst can start the moment the tier is cleared
     and simply play on through the re-render the gift causes. The swap and the
     re-insert are one synchronous step, so the loop's next frame finds it connected. */
  const firingCanvasElement = liveDrawer.querySelector('[data-kl-confetti].is-firing');

  liveDrawer.innerHTML = freshDrawer.innerHTML;

  if (!firingCanvasElement) return;

  const freshCanvasElement = liveDrawer.querySelector('[data-kl-confetti]');

  if (freshCanvasElement) freshCanvasElement.replaceWith(firingCanvasElement);
}

async function setGiftInCart(giftElement, isWanted) {
  const variantId = giftElement.dataset.klCartGiftVariant;

  if (!variantId) return;

  if (isGiftRequestInFlight) return;

  isGiftRequestInFlight = true;

  const { addUrl, changeUrl } = getCartApiUrls();

  try {
    await postCartChange(
      isWanted ? addUrl : changeUrl,
      isWanted ? { items: [{ id: variantId, quantity: 1 }] } : { id: variantId, quantity: 0 },
    );

    await renderCartDrawer();
  } catch (error) {
    const toggleElement = giftElement.querySelector('[data-kl-cart-gift-toggle]');

    if (toggleElement) toggleElement.checked = !isWanted;

    console.debug('Cart drawer gift toggle failed', { variantId, isWanted, error });
  } finally {
    isGiftRequestInFlight = false;
  }
}

document.addEventListener(
  'change',
  (event) => {
    const toggleElement = event.target.closest('[data-kl-cart-gift-toggle]');

    if (!toggleElement) return;

    event.stopPropagation();

    const giftElement = toggleElement.closest('[data-kl-cart-gift]');

    if (!giftElement) return;

    setGiftInCart(giftElement, toggleElement.checked);
  },
  { capture: true },
);

document.addEventListener('click', (event) => {
  const closeElement = event.target.closest('[data-kl-cart-gift-note-close]');

  if (!closeElement) return;

  const noteElement = closeElement.closest('[data-kl-cart-gift-note]');

  if (noteElement) noteElement.open = false;
});

function getOpenGiftNote() {
  return document.querySelector('[data-kl-cart-gift-note][open]');
}

const FREE_GIFT_DECLINED_KEY_PREFIX = 'kl-cart-free-gift-declined-';

let isFreeGiftRequestInFlight = false;

function getFreeGiftDeclinedKey(variantId) {
  return `${FREE_GIFT_DECLINED_KEY_PREFIX}${variantId}`;
}

function getIsFreeGiftDeclined(variantId) {
  try {
    return sessionStorage.getItem(getFreeGiftDeclinedKey(variantId)) === 'true';
  } catch (error) {
    console.debug('Cart drawer could not read the free gift decline', { variantId, error });
    return false;
  }
}

function setIsFreeGiftDeclined(variantId, isDeclined) {
  try {
    if (isDeclined) {
      sessionStorage.setItem(getFreeGiftDeclinedKey(variantId), 'true');
    } else {
      sessionStorage.removeItem(getFreeGiftDeclinedKey(variantId));
    }
  } catch (error) {
    console.debug('Cart drawer could not record the free gift decline', { variantId, isDeclined, error });
  }
}

function getIsFreeGiftEarned(threshold, cartTotal, rate) {
  if (!threshold) return false;
  if (Number.isNaN(cartTotal)) return false;

  return cartTotal >= getThresholdInCartCurrency(threshold, rate);
}

function getFreeGiftAction({ isEarned, isInCart, isDeclined }) {
  if (isEarned && !isInCart && !isDeclined) return 'add';
  if (!isEarned && isInCart) return 'remove';

  return null;
}

function getFreeGiftPlan(stateElements, rate) {
  const variantIdsToAdd = [];
  const variantIdsToRemove = [];

  stateElements.forEach((stateElement) => {
    const variantId = stateElement.dataset.klCartFreeGiftVariant;

    if (!variantId) {
      console.debug(
        'Cart drawer free gift: the product resolved but has no purchasable variant. Check it is in stock, or has inventory tracking off.',
        { productId: stateElement.dataset.klCartFreeGiftProduct },
      );
      return;
    }

    const isEarned = getIsFreeGiftEarned(
      Number.parseInt(stateElement.dataset.klCartFreeGiftThreshold, 10),
      Number.parseInt(stateElement.dataset.klCartTotal, 10),
      rate,
    );

    if (!isEarned) setIsFreeGiftDeclined(variantId, false);

    const action = getFreeGiftAction({
      isEarned,
      isInCart: stateElement.dataset.klCartFreeGiftInCart === 'true',
      isDeclined: getIsFreeGiftDeclined(variantId),
    });

    if (action === 'add' && !variantIdsToAdd.includes(variantId)) variantIdsToAdd.push(variantId);
    if (action === 'remove' && !variantIdsToRemove.includes(variantId)) variantIdsToRemove.push(variantId);
  });

  return { variantIdsToAdd, variantIdsToRemove };
}

async function syncFreeGifts() {
  if (isFreeGiftRequestInFlight) return;

  const stateElements = Array.from(document.querySelectorAll('[data-kl-cart-free-gift]'));

  if (!stateElements.length) return;

  const { variantIdsToAdd, variantIdsToRemove } = getFreeGiftPlan(stateElements, getCurrencyRate());

  if (!variantIdsToAdd.length && !variantIdsToRemove.length) return;

  isFreeGiftRequestInFlight = true;

  const { addUrl, changeUrl } = getCartApiUrls();

  try {
    if (variantIdsToAdd.length) {
      await postCartChange(addUrl, {
        items: variantIdsToAdd.map((variantId) => ({ id: variantId, quantity: 1 })),
      });
    }

    for (const variantId of variantIdsToRemove) {
      await postCartChange(changeUrl, { id: variantId, quantity: 0 });
    }

    await renderCartDrawer();
  } catch (error) {
    console.debug('Cart drawer free gift sync failed', { variantIdsToAdd, variantIdsToRemove, error });
  } finally {
    isFreeGiftRequestInFlight = false;

    /* The pass answering the gift's own re-render runs before this line — the observer
       is a microtask, the line after an await is a later one — so it reads the flag as
       still set, holds the strip back, and notes nothing. That pass was the occasion
       the held tier was waiting for, and it is spent. Releasing it here rather than
       leaving it to whichever mutation happens along next: the tier and its burst are
       owed to the cart as it stands now, and nothing about that is still in doubt. */
    /* A burst owed from before that could not be drawn when it was offered — the drawer
       was shut, or its canvas was mid-burst — is not one the tier count will offer
       again, because that count has not moved. This is the one other occasion worth
       asking on: the cart has just settled and the drawer has just been re-rendered. */
    flushCartConfetti();
  }
}

function applyShippingState(rowElement) {
  const threshold = Number.parseFloat(rowElement.dataset.klCartShippingThreshold) || 0;
  const eligibleTotal = Number.parseInt(rowElement.dataset.klCartShippingTotal, 10) || 0;

  const isFree =
    threshold > 0 && eligibleTotal >= getThresholdInCartCurrency(threshold, getCurrencyRate());

  rowElement.classList.toggle('is-free', isFree);
}

function initCartDrawer() {
  noteCartRewardsAbsent();
  document.querySelectorAll('[data-kl-cart-rewards]').forEach(applyRewardProgress);
  document.querySelectorAll('[data-kl-cart-shipping]').forEach(applyShippingState);
  syncFreeGifts();

  /* After the gift has gone in and this markup is the markup that stays, a burst held
     back for it can finally play out. */
  flushCartConfetti();
}

document.addEventListener('click', (event) => {
  const declineElement = event.target.closest('[data-kl-cart-free-gift-decline]');

  if (!declineElement) return;

  const variantId = declineElement.dataset.klCartFreeGiftDecline;

  if (!variantId) return;

  setIsFreeGiftDeclined(variantId, true);
});

initCartDrawer();

document.querySelectorAll('[data-kl-cart-drawer], [data-kl-cart-page]').forEach((cartRootElement) => {
  new MutationObserver(() => initCartDrawer()).observe(cartRootElement, {
    childList: true,
    subtree: true,
  });

  /* Dawn opens the drawer by putting `active` on this element, one tick after it has
     swapped in the new markup. That class landing is both the moment a burst owed
     from the swap can finally be seen, and an occasion for one in its own right. */
  new MutationObserver(() => noteCartRootClassChange(cartRootElement)).observe(cartRootElement, {
    attributes: true,
    attributeFilter: ['class'],
  });
});

document.addEventListener(
  'keyup',
  (event) => {
    if (event.code !== 'Escape') return;

    const openNoteElement = getOpenGiftNote();

    if (!openNoteElement) return;

    event.stopPropagation();
    openNoteElement.open = false;
  },
  { capture: true },
);

document.addEventListener('click', (event) => {
  const openNoteElement = getOpenGiftNote();

  if (!openNoteElement) return;
  if (openNoteElement.contains(event.target)) return;

  openNoteElement.open = false;
});

document.addEventListener('shopify:section:load', () => initCartDrawer());
